import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Skeleton, Typography } from "@mui/material";

import useIsOwnProfile from "../../../../utils/useIsOwnProfile";
import { getEntityProfileBranchRoute, getEntityProfileEditBranchRoute } from "../../../../../../routers/routeUtils";

import { useDeleteEntityBranch } from "../../../../../Entity/api/mutations/branch.mutation";

import { EditLocationButtonIcon, LocationIcon, RemoveLocationButtonIcon } from "../../../../assets/icons/entity-icons";

export const BranchItem = ({
  id,
  name,
  address,
  isHeadquarter,
  googleMapsLink,
  canModify,
}: {
  id: number;
  name: string;
  address: string;
  isHeadquarter: boolean;
  googleMapsLink: string;
  canModify: boolean;
}) => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityLocations" });
  const navigate = useNavigate();

  const { mutate: deleteBranch, isPending: isDeletingBranch } = useDeleteEntityBranch(() =>
    toast.success(t("branchDeleted"))
  );

  const handleRemoveBranch = () => {
    deleteBranch(String(id));
  };
  const handleEditBranch = () => navigate(getEntityProfileEditBranchRoute(entityId!, id));
  const handleViewAllDetails = () => navigate(getEntityProfileBranchRoute(entityId!, id));

  const { entityId } = useParams();
  const isOwnProfile = useIsOwnProfile({ entityId: Number(entityId), isEntityIdLoading: false });

  const { isOwn, isLoading: isLoadingIsOwnProfile } = isOwnProfile;
  return (
    <Box key={id} sx={styles.branchItem}>
      <LocationIcon />
      {isHeadquarter && <Typography sx={styles.headquarterLabel}>{t("headquarters")}</Typography>}
      <Box sx={styles.branchItemContent}>
        <Typography sx={styles.branchItemName}>{name}</Typography>
        <Typography sx={styles.branchItemAddress}>{address}</Typography>
        <Box sx={styles.branchButtonsContainer}>
          {isOwn && !isLoadingIsOwnProfile && canModify && (
            <>
              <Button
                variant="text-md"
                startIcon={<RemoveLocationButtonIcon />}
                sx={styles.removeBranchButton}
                onClick={handleRemoveBranch}
                disabled={isDeletingBranch}
              >
                {t("removeLocation")}
              </Button>
              <Button
                variant="text-md"
                startIcon={<EditLocationButtonIcon />}
                sx={styles.editBranchButton}
                onClick={handleEditBranch}
              >
                {t("editDetails")}
              </Button>
            </>
          )}

          {!canModify && (
            <>
              <Button variant="text-md" component="a" href={googleMapsLink} sx={styles.viewAllDetailsButton}>
                {t("viewOnMap")}
              </Button>
              <Button variant="text-md" onClick={handleViewAllDetails} sx={styles.viewAllDetailsButton}>
                {t("viewAllDetails")}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const BranchItemSkeleton = () => {
  return (
    <Box sx={styles.branchItem}>
      <Skeleton variant="circular" width={24} height={24} />
      <Box sx={styles.branchItemContent}>
        <Skeleton variant="text" width={180} height={24} sx={styles.branchItemName} />
        <Skeleton variant="text" width={240} height={20} sx={styles.branchItemAddress} />
        <Box sx={styles.branchButtonsContainer}>
          <Skeleton variant="text" width={120} height={20} />
          <Skeleton variant="text" width={100} height={20} />
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  branchItem: {
    display: "flex",
    gap: "12px",
    position: "relative",
  },
  branchItemContent: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  branchItemName: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  branchItemAddress: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Inter",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  branchButtonsContainer: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: "24px",
    paddingTop: "10px",
  },
  removeBranchButton: {
    color: "var(--Red---200, #CC3E3E) !important",
    backgroundColor: "transparent !important",
    padding: 0,
  },
  editBranchButton: {
    color: "var(--Brand-700, #076D0D) !important",
    backgroundColor: "transparent !important",
    padding: 0,
    width: "fit-content",
  },
  viewAllDetailsButton: {
    color: "var(--Brand-700, #076D0D) !important",
    backgroundColor: "transparent !important",
    padding: 0,
  },
  headquarterLabel: {
    position: "absolute",
    top: 0,
    right: 0,
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    textAlign: "center",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",

    padding: "2px 8px",
    borderRadius: "16px",
    border: "1px solid var(--Gray-200, #E4E7EC)",
    background: "var(--Gray-50, #F9FAFB)",
  },
};
